import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaMountain } from 'react-icons/fa';
import { companyInfo } from '../../data/tourData';
import BookingForm from '../../components/common/BookingForm';
import './TourDetail.css';

const DayaraBugyal = () => {
  const tourData = {
    name: "Dayara Bugyal Trek",
    duration: "03 Nights 04 Days",
    price: "₹ 5,999/-",
    image: "https://res.cloudinary.com/duym2bgp2/image/upload/v1754935198/thfz6pxxfavwgzoixjqo.jpg",
    altitude: "11,600 ft",
    difficulty: "Easy",
    region: "Uttarakhand",
    highlights: ["Alpine meadows", "Family trek", "Scenic beauty"],
    bestTime: "May to November",
    description: "The Dayara Bugyal Trek is a perfect family-friendly trek that takes you to one of the most beautiful alpine meadows in Uttarakhand. This easy trek offers stunning views of snow-capped peaks, lush green meadows, and pristine natural beauty."
  };

  return (
    <>
      <Helmet>
        <title>Dayara Bugyal Trek - NomadTrailz</title>
        <meta name="description" content="Experience the beautiful Dayara Bugyal Trek in Uttarakhand. Perfect family trek to stunning alpine meadows with breathtaking mountain views." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <section className="tour-hero">
          <div className="hero-background">
            <img
              src={tourData.image}
              alt={tourData.name}
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {tourData.name}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              A perfect family trek to stunning alpine meadows
            </motion.p>
          </div>
        </section>

        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div
                  className="info-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2>Tour Overview</h2>
                  <p>{tourData.description}</p>
                  <p>
                    This trek is ideal for beginners and families looking for a gentle introduction to 
                    Himalayan trekking. The Dayara Bugyal offers vast open meadows that stretch as far as 
                    the eye can see, providing a perfect setting for relaxation and photography. The 
                    trek takes you through dense forests, open grasslands, and offers panoramic views 
                    of the surrounding mountain ranges.
                  </p>
                </motion.div>

                <motion.div
                  className="highlights-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Highlights</h3>
                  <ul>
                    <li>Stunning alpine meadows</li>
                    <li>Perfect family-friendly trek</li>
                    <li>Scenic natural beauty</li>
                    <li>Snow-capped peak views</li>
                    <li>Dense oak woodlands</li>
                    <li>Photography opportunities</li>
                    <li>Beginner-friendly terrain</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="itinerary-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 1: From Dehradun to Raithal</h4>
                    <ul>
                      <li>Our team will meet you directly at the bus stop or train station, and we will assist you in making the transfer from Dehradun to Raithal.</li>
                      <li>Consequently, your journey will be made seamless and comfortable.</li>
                      <li>Raithal, a little town situated twenty kilometers above the city of Uttarkashi, serves as the beginning point of the trip.</li>
                      <li>In other words, it is where your trek adventure will start.</li>
                      <li>In Raithal, you will stay at the homestay, and a briefing and introduction session with the person leading the trek, as well as the other people traveling with you, will be held.</li>
                      <li>This session will, above all, ensure everyone is prepared for the journey ahead.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: From Raithal to Gui</h4>
                    <ul>
                      <li>Following breakfast we will head off for the actual trek to Gui and although the trek is still on we are rewarded with an opportunity to view the Oak Woodland plus the scenic prospect of the Mountain that bears the varied greenery cover at the summit.</li>
                      <li>As a result, the traveling is even more delightful and the trip is more satisfying.</li>
                      <li>After you get to the campgrounds, you can take a walk around in the evening so as to assess the terrain around.</li>
                      <li>In other words, it permits you to relax and have an interaction with nature.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 3: From Gui to Summit and back</h4>
                    <ul>
                      <li>After breakfast, you will go on a trek that is simple to moderate to reach Dayara Top.</li>
                      <li>During this trek, you will go through woodlands, open out to expansive meadows, and take in vistas of mountains that are beautifully green.</li>
                      <li>Most importantly, the journey offers a chance to connect with nature's untouched beauty.</li>
                      <li>After dinner, make your way down to the campground, where you will spend the night.</li>
                      <li>As you make your way down this path, you will find yourself traversing meadows that are bursting with vivid colours.</li>
                      <li>In other words, the landscape on the descent is just as stunning as the ascent.</li>
                      <li>Even if the last slope is hard, the spectacular view of the Dodital range makes it worthwhile to put in the effort to climb through it.</li>
                      <li>Consequently, the breathtaking views more than compensate for the challenge of conquering the final stretch.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 4: From Gui to Dehradun</h4>
                    <ul>
                      <li>Next morning at early hours, after our breakfast, we had to leave this beautiful spot and proceed through the same route from Gui to Raithal through the forest.</li>
                      <li>Finally, you will again get opportunity to observe the nature and beauty of the place while on a trek.</li>
                      <li>The driver will pick you up upon your arrival in Raithal and assist in transferring you to Dehradun.</li>
                      <li>In other words, there will not be any problems when transitioning back to Dehradun.</li>
                      <li>This is where your journey will end and you will leave with some good memories of your trip as a look.</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div
                  className="things-to-carry-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Things To Carry</h3>
                  <p className="carry-intro">All items listed are essential, unless marked as optional. Items are divided into five categories for your convenience.</p>
                  
                  <div className="carry-categories">
                    <div className="carry-category">
                      <h4><i className="fas fa-hiking"></i> Trekking Gear</h4>
                      <ul>
                        <li>Rucksack with rain cover</li>
                        <li>Day pack bag</li>
                        <li>Head torch with spare batteries</li>
                        <li>UV protection sunglasses</li>
                        <li>Water bottles: Two 1-liter bottles</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-shoe-prints"></i> Footwear</h4>
                      <ul>
                        <li>Non-skid, deep treaded, high-ankle trekking shoes: 1 pair</li>
                        <li>Lightweight slippers or sandals: 1 pair</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-tshirt"></i> Clothing</h4>
                      <ul>
                        <li>Quick-dry warm lower or track pants: 2</li>
                        <li>Full-sleeve T-shirts/sweatshirts: 1 for every 2 days of trekking</li>
                        <li>Thick woolen socks: 1 pair for every 2 days of trekking</li>
                        <li>Thermal body warmer (upper & lower): 1 set</li>
                        <li>Undergarments: 1 for each day of trekking</li>
                        <li>Warm jacket (closed at wrist & neck): 1</li>
                        <li>Full-sleeve sweater: 1</li>
                        <li>Rainwear (jacket & pants): 1 set</li>
                        <li>Waterproof, warm gloves: 1 pair</li>
                        <li>Woolen cap: 1</li>
                        <li>Sun-shielding hat: 1</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-soap"></i> Toiletries</h4>
                      <ul>
                        <li>Personal toiletries kit: Small towel, toilet paper, paper soap, bar soap, toothbrush, toothpaste, cold cream, etc.</li>
                        <li>Sunscreen lotion: Small pack</li>
                        <li>Lip balm: Small pack</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-utensils"></i> Utensils</h4>
                      <ul>
                        <li>Small, lightweight, leak-proof lunch box: 1</li>
                        <li>Plate: 1</li>
                        <li>Spoon: 1</li>
                        <li>Tea/coffee mug (plastic): 1</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-plus-circle"></i> Miscellaneous</h4>
                      <ul>
                        <li className="optional">Camera: (Optional)</li>
                        <li>Personal medicines: Bring plenty if needed; consult your doctor before the trek.</li>
                        <li className="optional">Dry fruits, nuts, chocolate bars (Optional)</li>
                      </ul>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li>Pick and Drop Transfers from/to Dehradun</li>
                    <li>All meals are included with proper hygiene parameters</li>
                    <li>Triple/double sharing tent accommodations are available</li>
                    <li>During the trek, there will be morning and evening tea with a small refreshment</li>
                    <li>All tolls and forest fees</li>
                    <li>Trekking gears (tents, sleeping bags, ropes, gaiters, crampons etc.)</li>
                    <li>First Aid Medical Kit</li>
                    <li>Expert Guide</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 1.0 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li>Food during transit</li>
                    <li>Any kind of personal expenses</li>
                    <li>Any kind of personal medication</li>
                    <li>Transportation to and from Dehradun</li>
                    <li>Personal trekking gear and equipment</li>
                    <li>Any additional expenses due to weather conditions or unforeseen circumstances</li>
                    <li>Anything that has not been covered in the inclusions mentioned above</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div
                  className="booking-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">{tourData.price}</span>
                    <span className="duration">{tourData.duration}</span>
                  </div>

                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>{tourData.region}</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: {tourData.bestTime}</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 8-12 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: {tourData.difficulty}</span>
                    </div>
                    <div className="feature">
                      <FaMountain />
                      <span>Altitude: {tourData.altitude}</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="quick-info-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Quick Info</h3>
                  <div className="info-item">
                    <FaMountain />
                    <div>
                      <strong>Max Altitude:</strong>
                      <span>11,600 feet</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaMapMarkerAlt />
                    <div>
                      <strong>Trek Distance:</strong>
                      <span>16 km (round trip)</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaStar />
                    <div>
                      <strong>Difficulty Level:</strong>
                      <span>Easy</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaCalendarAlt />
                    <div>
                      <strong>Best Season:</strong>
                      <span>May to November</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="cost-details-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Cost Details</h3>
                  <h4>Cost Per Person (Ex. Dehradun)</h4>
                  <ul>
                    <li><strong>Quad Sharing:</strong> ₹ 5,999 + 5% GST</li>
                    <li><strong>Triple Sharing:</strong> ₹ 6,499 + 5% GST</li>
                    <li><strong>Double Sharing:</strong> ₹ 6,999 + 5% GST</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="pickup-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Pickup Points</h3>
                  <ul>
                    <li><strong>Dehradun:</strong> Dehradun bus stand or railway station between 08:00 AM to 09:00 AM</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="tour-highlights-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Tour Highlights</h3>
                  <ul>
                    <li>Stunning alpine meadows</li>
                    <li>Perfect family-friendly trek</li>
                    <li>Scenic natural beauty</li>
                    <li>Snow-capped peak views</li>
                    <li>Dense oak woodlands</li>
                    <li>Photography opportunities</li>
                    <li>Beginner-friendly terrain</li>
                    <li>Expert guide with local knowledge</li>
                  </ul>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName={tourData.name}
                  tourPrice={tourData.price}
                  tourDuration={tourData.duration}
                />
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default DayaraBugyal;
